import { useState } from "react";
import { Save, Smartphone, UserCheck, Link as LinkIcon, ExternalLink, Copy, Sparkles, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";
import { copyToClipboard } from "../../lib/clipboard";

const DEFAULT_PLAYER_PATH = "/downloads/maxlive88-app.apk";
const DEFAULT_AGENT_PATH = "/downloads/maxlive88-agent.apk";

const FEATURE_ICONS = ["Zap","Star","ShieldCheck","Sparkles","Bell","Award","Trophy","Crown","Smartphone","Download"];

const DEFAULT_FEATURES = [
  { icon: "Zap",         title: "2× Faster Loading",   color: "#ffc933" },
  { icon: "Star",        title: "Exclusive App Bonus", color: "#19a55c" },
  { icon: "ShieldCheck", title: "Bank-grade Secure",   color: "#3b82f6" },
  { icon: "Sparkles",    title: "Real-time Alerts",    color: "#ec4899" },
];
const DEFAULT_STEPS = [
  { n: "1", title: "Download APK",   desc: "Tap the download button — the .apk file saves to your device." },
  { n: "2", title: "Enable installs",desc: "In settings, allow \"Install unknown apps\" for your browser." },
  { n: "3", title: "Open & Play",    desc: "Tap the downloaded file, install, sign in and start winning." },
];

export default function ConfigApk() {
  const { form, set, save, loading } = useSettings();
  const [saving, setSaving] = useState(false);

  if (!form)
    return (
      <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">
        Loading...
      </div>
    );

  const onSave = async () => {
    setSaving(true);
    await save();
    setSaving(false);
  };

  const siteOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const playerEffective = (form.apk_player_url || "").trim() || `${siteOrigin}${DEFAULT_PLAYER_PATH}`;
  const agentEffective = (form.apk_agent_url || "").trim() || `${siteOrigin}${DEFAULT_AGENT_PATH}`;

  const copyUrl = async (url) => {
    const ok = await copyToClipboard(url);
    if (ok) toast.success("Link copied"); else toast.error("Could not copy");
  };

  // ---- App Download landing-page editor helpers (apk_features / apk_steps) ----
  const features = Array.isArray(form.apk_features) && form.apk_features.length ? form.apk_features : DEFAULT_FEATURES;
  const steps    = Array.isArray(form.apk_steps)    && form.apk_steps.length    ? form.apk_steps    : DEFAULT_STEPS;
  const updateFeature = (i, patch) => set("apk_features", features.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const removeFeature = (i) => set("apk_features", features.filter((_, idx) => idx !== i));
  const addFeature    = () => set("apk_features", [...features, { icon: "Zap", title: "New feature", color: "#19a55c" }]);
  const updateStep = (i, patch) => set("apk_steps", steps.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeStep = (i) => set("apk_steps", steps.filter((_, idx) => idx !== i));
  const addStep    = () => set("apk_steps", [...steps, { n: String(steps.length + 1), title: "New step", desc: "" }]);

  return (
    <div data-testid="config-apk-page">
      <PageHeader
        title="Configuration"
        subtitle="APK Downloads · control Player App, Agent App + the public /app-download landing page"
      />
      <ConfigTabs />

      <div className="space-y-5 max-w-5xl">
        {/* ----------- App Download landing-page (/app-download) ----------- */}
        <Section title="App Download Landing Page (/app-download)" icon={Sparkles} accent="emerald">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Primary APK URL (used by hero Download button)">
              <input
                type="text"
                value={form.apk_url || ""}
                onChange={(e) => set("apk_url", e.target.value)}
                placeholder="https://cdn.example.com/maxlive88.apk"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm font-mono"
                data-testid="cfg-apk-url"
              />
            </Field>
            <Field label="Version label (e.g. v2.0)">
              <input
                type="text"
                value={form.apk_version || ""}
                onChange={(e) => set("apk_version", e.target.value)}
                placeholder="v2.0"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-version-label"
              />
            </Field>
            <Field label="Installer size text (e.g. 32 MB)">
              <input
                type="text"
                value={form.apk_size_mb || ""}
                onChange={(e) => set("apk_size_mb", e.target.value)}
                placeholder="32 MB"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-size-mb"
              />
            </Field>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Star rating shown under hero (e.g. 4.8)">
              <input
                type="text"
                value={form.apk_rating || ""}
                onChange={(e) => set("apk_rating", e.target.value)}
                placeholder="4.8"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-rating"
              />
            </Field>
            <Field label="Downloads count text (e.g. 500k+)">
              <input
                type="text"
                value={form.apk_downloads || ""}
                onChange={(e) => set("apk_downloads", e.target.value)}
                placeholder="500k+"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-downloads"
              />
            </Field>
          </div>

          {/* Features editor */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                Hero feature pills · {features.length} item{features.length === 1 ? "" : "s"}
              </div>
              <button
                type="button"
                onClick={addFeature}
                className="inline-flex items-center gap-1 h-7 px-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-sm text-[11px] font-semibold"
                data-testid="cfg-apk-feature-add"
              >
                <Plus size={11} /> Add feature
              </button>
            </div>
            <div className="space-y-2">
              {features.map((f, i) => (
                <div key={i} className="flex items-end gap-2 border border-gray-200 rounded-sm p-2 bg-gray-50/40" data-testid={`cfg-apk-feature-${i}`}>
                  <div className="w-32 shrink-0">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Icon</label>
                    <select
                      value={f.icon || "Zap"}
                      onChange={(e) => updateFeature(i, { icon: e.target.value })}
                      className="w-full h-9 px-2 border border-gray-200 rounded-sm text-xs"
                    >
                      {FEATURE_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Title</label>
                    <input
                      value={f.title || ""}
                      onChange={(e) => updateFeature(i, { title: e.target.value })}
                      className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm"
                      placeholder="Feature title"
                    />
                  </div>
                  <div className="w-32 shrink-0">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Color</label>
                    <div className="flex gap-1">
                      <input
                        type="color"
                        value={f.color || "#19a55c"}
                        onChange={(e) => updateFeature(i, { color: e.target.value })}
                        className="h-9 w-10 border border-gray-200 rounded-sm"
                      />
                      <input
                        value={f.color || ""}
                        onChange={(e) => updateFeature(i, { color: e.target.value })}
                        className="flex-1 h-9 px-2 border border-gray-200 rounded-sm text-xs font-mono"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFeature(i)}
                    className="h-9 w-9 grid place-items-center text-rose-500 hover:bg-rose-50 rounded-sm shrink-0"
                    data-testid={`cfg-apk-feature-del-${i}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Steps editor */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                Install steps · {steps.length} item{steps.length === 1 ? "" : "s"}
              </div>
              <button
                type="button"
                onClick={addStep}
                className="inline-flex items-center gap-1 h-7 px-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-sm text-[11px] font-semibold"
                data-testid="cfg-apk-step-add"
              >
                <Plus size={11} /> Add step
              </button>
            </div>
            <div className="space-y-2">
              {steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2 border border-gray-200 rounded-sm p-2 bg-gray-50/40" data-testid={`cfg-apk-step-${i}`}>
                  <div className="w-16 shrink-0">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">No.</label>
                    <input
                      value={s.n || ""}
                      onChange={(e) => updateStep(i, { n: e.target.value })}
                      className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm tabular text-center"
                      placeholder="1"
                    />
                  </div>
                  <div className="w-44 shrink-0">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Title</label>
                    <input
                      value={s.title || ""}
                      onChange={(e) => updateStep(i, { title: e.target.value })}
                      className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm"
                      placeholder="Step title"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Description</label>
                    <input
                      value={s.desc || ""}
                      onChange={(e) => updateStep(i, { desc: e.target.value })}
                      className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm"
                      placeholder="Short description"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeStep(i)}
                    className="mt-5 h-9 w-9 grid place-items-center text-rose-500 hover:bg-rose-50 rounded-sm shrink-0"
                    data-testid={`cfg-apk-step-del-${i}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <a
              href="/app-download"
              target="_blank"
              rel="noopener noreferrer"
              className="h-9 px-3 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5"
            >
              <ExternalLink size={12} /> Preview /app-download
            </a>
          </div>
        </Section>

        {/* Player APK */}
        <Section title="Player App APK" icon={Smartphone} accent="emerald">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Toggle
              label="Player APK download enabled"
              hint="When OFF, /download hides the Player tab and APK button"
              checked={!!form.apk_player_enabled}
              onChange={(v) => set("apk_player_enabled", v)}
              testid="cfg-apk-player-enabled"
            />
            <Field label="App label (shown on download page)">
              <input
                type="text"
                value={form.apk_player_label || ""}
                onChange={(e) => set("apk_player_label", e.target.value)}
                placeholder="Maxlive88 App"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-player-label"
              />
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Download URL (leave BLANK to use built-in file on this server)">
              <input
                type="text"
                value={form.apk_player_url || ""}
                onChange={(e) => set("apk_player_url", e.target.value)}
                placeholder={DEFAULT_PLAYER_PATH}
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm font-mono"
                data-testid="cfg-apk-player-url"
              />
              <div className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
                Accepts an absolute URL (e.g. <code className="text-gray-700">https://cdn.example.com/app.apk</code>) or a path starting with <code className="text-gray-700">/</code> (served from this VPS).
                If left blank, players will download the built-in file at <code className="text-gray-700">{DEFAULT_PLAYER_PATH}</code>.
              </div>
            </Field>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Version">
              <input
                type="text"
                value={form.apk_player_version || ""}
                onChange={(e) => set("apk_player_version", e.target.value)}
                placeholder="1.0"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-player-version"
              />
            </Field>
            <Field label="Installer size (MB)">
              <input
                type="number"
                step="0.01"
                min={0.01}
                value={form.apk_player_size_kb ? (form.apk_player_size_kb / 1024).toFixed(2) : ""}
                onChange={(e) => set("apk_player_size_kb", Math.round((Number(e.target.value) || 0) * 1024))}
                placeholder="e.g. 6.68"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-player-size"
              />
            </Field>
          </div>

          <EffectiveUrlPreview url={playerEffective} onCopy={() => copyUrl(playerEffective)} testid="cfg-apk-player-preview" />
        </Section>

        {/* Agent APK */}
        <Section title="Agent App APK" icon={UserCheck} accent="amber">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Toggle
              label="Agent APK download enabled"
              hint="When OFF, /download hides the Agent tab"
              checked={!!form.apk_agent_enabled}
              onChange={(v) => set("apk_agent_enabled", v)}
              testid="cfg-apk-agent-enabled"
            />
            <Field label="App label">
              <input
                type="text"
                value={form.apk_agent_label || ""}
                onChange={(e) => set("apk_agent_label", e.target.value)}
                placeholder="Maxlive88 Agent"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-agent-label"
              />
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Download URL (leave BLANK to use built-in file on this server)">
              <input
                type="text"
                value={form.apk_agent_url || ""}
                onChange={(e) => set("apk_agent_url", e.target.value)}
                placeholder={DEFAULT_AGENT_PATH}
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm font-mono"
                data-testid="cfg-apk-agent-url"
              />
              <div className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
                Accepts an absolute URL or path starting with <code className="text-gray-700">/</code>. Blank = built-in file at <code className="text-gray-700">{DEFAULT_AGENT_PATH}</code>.
              </div>
            </Field>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Version">
              <input
                type="text"
                value={form.apk_agent_version || ""}
                onChange={(e) => set("apk_agent_version", e.target.value)}
                placeholder="1.0"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-agent-version"
              />
            </Field>
            <Field label="Installer size (MB)">
              <input
                type="number"
                step="0.01"
                min={0.01}
                value={form.apk_agent_size_kb ? (form.apk_agent_size_kb / 1024).toFixed(2) : ""}
                onChange={(e) => set("apk_agent_size_kb", Math.round((Number(e.target.value) || 0) * 1024))}
                placeholder="e.g. 6.59"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm"
                data-testid="cfg-apk-agent-size"
              />
            </Field>
          </div>

          <EffectiveUrlPreview url={agentEffective} onCopy={() => copyUrl(agentEffective)} testid="cfg-apk-agent-preview" />
        </Section>

        {/* Info card */}
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 text-xs text-blue-800 leading-relaxed">
          <div className="font-semibold mb-1">Where the built-in APK comes from</div>
          The built-in file lives at <code>/app/frontend/public/downloads/maxlive88-app.apk</code> (and <code>...-agent.apk</code>) on this VPS.
          You can either:
          <ol className="list-decimal ml-5 mt-1.5 space-y-0.5">
            <li>Upload a pre-built APK to that folder (FTP/SCP) and keep URL blank — the download page will pick it up automatically.</li>
            <li>Host the APK on a CDN / S3 / Cloudflare R2 and paste the full <code>https://...</code> URL here.</li>
            <li>Rebuild the bundled WebView APK on the VPS with <code>bash /app/apk-build/build_user.sh</code> (uses <code>APP_URL</code> env var for the target site URL).</li>
          </ol>
        </div>

        {/* Save */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onSave}
            disabled={loading || saving}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="cfg-apk-save"
          >
            <Save size={14} /> {saving || loading ? "Saving..." : "Save APK Settings"}
          </button>
          <a
            href="/download"
            target="_blank"
            rel="noopener noreferrer"
            className="h-10 px-4 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-sm text-sm font-semibold inline-flex items-center gap-2"
            data-testid="cfg-apk-preview-page"
          >
            <ExternalLink size={14} /> Preview /download
          </a>
        </div>
      </div>
    </div>
  );
}

function EffectiveUrlPreview({ url, onCopy, testid }) {
  return (
    <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-sm" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1 flex items-center gap-1.5">
        <LinkIcon size={10} /> Effective download URL
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 font-mono text-[12px] text-gray-800 break-all">{url}</div>
        <button
          type="button"
          onClick={onCopy}
          className="shrink-0 h-7 px-2 border border-gray-200 bg-white hover:bg-gray-50 rounded-sm inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700"
        >
          <Copy size={11} /> Copy
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 h-7 px-2 border border-gray-200 bg-white hover:bg-gray-50 rounded-sm inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700"
        >
          <ExternalLink size={11} /> Test
        </a>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Section({ title, icon: Icon, accent, children }) {
  const accentColor = accent === "amber" ? "text-amber-500" : "text-emerald-500";
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        {Icon && <Icon size={14} className={accentColor} />}
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5" />
      <div>
        <div className="text-sm font-semibold text-gray-900">{label}</div>
        {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}
